import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, Grid, TextField, IconButton, Dialog,
  Tooltip, Tabs, Tab, Switch, Autocomplete, InputAdornment,
} from "@mui/material";
import { useParams } from "react-router-dom";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface Vendor {
  id: number; customerCompanyName: string; customerCompanyEmail: string; customerCompanyPhoneNumebr: string;
  customerFirstName: string; customerLastName: string; customerEmail: string; customerMobileNum: string;
  customerCity: string; customerProvince: string; customerAddress: string; customerPostalCode: string;
  customerStreetNumber: string; customerStreetName: string; customerDescription: string;
}

interface SupplierProduct {
  id: number; supplier_id: number; product_id: number; supplier_price: number; supplier_unit: string;
  unit_conversion: string; minimum_order_qty: number; lead_time_days: number; is_default: boolean;
  product?: { id: number; productName: string; productBarcode: string; productPrice: number; salePrice: number; wholesaleprice: number; };
}

interface ProductOption { id: number; productName: string; productBarcode: string; }

const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };

const VendorView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<SupplierProduct | null>(null);
  const [form, setForm] = useState({ product_id: 0, supplier_price: 0, supplier_unit: "", unit_conversion: "", minimum_order_qty: 0, lead_time_days: 0, is_default: false });
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState<ProductOption[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/vendor/${id}/products`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) { setProducts(json.data || []); setVendor(json.vendor || null); }
    } catch {} finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAllProducts = async () => {
    if (allProducts.length > 0) return;
    try {
      const res = await fetch(`${API_URL}/product/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "", id: "" }) });
      const json = await res.json();
      setAllProducts((json.data || []).filter((p: any) => !p.isDelete).map((p: any) => ({ id: p.id, productName: p.productName, productBarcode: p.productBarcode || "" })));
    } catch {}
  };

  const openAdd = () => {
    setEditItem(null);
    setForm({ product_id: 0, supplier_price: 0, supplier_unit: "", unit_conversion: "", minimum_order_qty: 0, lead_time_days: 0, is_default: false });
    setSelectedProduct(null);
    loadAllProducts();
    setDialogOpen(true);
  };

  const openEdit = (item: SupplierProduct) => {
    setEditItem(item);
    setForm({ product_id: item.product_id, supplier_price: item.supplier_price, supplier_unit: item.supplier_unit || "", unit_conversion: item.unit_conversion || "", minimum_order_qty: item.minimum_order_qty, lead_time_days: item.lead_time_days, is_default: item.is_default });
    setSelectedProduct(item.product ? { id: item.product.id, productName: item.product.productName, productBarcode: item.product.productBarcode || "" } : null);
    loadAllProducts();
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.product_id && !editItem) return;
    setSaving(true);
    try {
      const url = editItem
        ? `${API_URL}/vendor/${id}/products/${editItem.id}`
        : `${API_URL}/vendor/${id}/products`;
      const method = editItem ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success) { setDialogOpen(false); loadData(); }
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (mappingId: number) => {
    if (!window.confirm("Remove this product from supplier?")) return;
    try {
      await fetch(`${API_URL}/vendor/${id}/products/${mappingId}`, { method: "DELETE", headers: getHeaders() });
      loadData();
    } catch {}
  };

  const address = vendor ? [vendor.customerStreetNumber, vendor.customerStreetName, vendor.customerCity, vendor.customerProvince, vendor.customerPostalCode].filter(Boolean).join(", ") : "";

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
      <CircularProgress size={28} sx={{ color: "#00838f" }} />
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, background: "linear-gradient(135deg, #1e293b, #334155)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <StorefrontIcon sx={{ fontSize: 26, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={24} fontWeight={800} color="#0f172a" letterSpacing="-0.03em">{vendor?.customerCompanyName || "Supplier"}</Typography>
          <Typography fontSize={13} color="#94a3b8">{vendor?.customerEmail || vendor?.customerCompanyEmail || ""}</Typography>
        </Box>
        <Chip label={`${products.length} Products`} sx={{ height: 28, fontSize: 12, fontWeight: 700, bgcolor: "#f0fdf4", color: "#16a34a" }} />
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13, minHeight: 40 }, "& .Mui-selected": { color: "#0f172a !important" }, "& .MuiTabs-indicator": { bgcolor: "#0f172a", height: 2.5, borderRadius: 2 } }}>
        <Tab label="Profile" icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
        <Tab label="Supplied Products" icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ gap: 0.75 }} />
      </Tabs>

      {/* ── Profile Tab ── */}
      {tab === 0 && vendor && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography fontSize={14} fontWeight={700} color="#0f172a" mb={2}>Contact Information</Typography>
              {[
                { icon: <PersonIcon sx={{ fontSize: 16 }} />, label: "Name", value: [vendor.customerFirstName, vendor.customerLastName].filter(Boolean).join(" ") || "—" },
                { icon: <EmailIcon sx={{ fontSize: 16 }} />, label: "Email", value: vendor.customerEmail || vendor.customerCompanyEmail || "—" },
                { icon: <PhoneIcon sx={{ fontSize: 16 }} />, label: "Phone", value: vendor.customerMobileNum || vendor.customerCompanyPhoneNumebr || "—" },
                { icon: <LocationOnIcon sx={{ fontSize: 16 }} />, label: "Address", value: address || "—" },
              ].map((row, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderBottom: i < 3 ? "1px solid #f1f5f9" : "none" }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>{row.icon}</Box>
                  <Box>
                    <Typography fontSize={10} color="#94a3b8" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{row.label}</Typography>
                    <Typography fontSize={13} color="#1e293b" fontWeight={500}>{row.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography fontSize={14} fontWeight={700} color="#0f172a" mb={2}>Company Details</Typography>
              {[
                { label: "Company", value: vendor.customerCompanyName || "—" },
                { label: "Company Email", value: vendor.customerCompanyEmail || "—" },
                { label: "Company Phone", value: vendor.customerCompanyPhoneNumebr || "—" },
                { label: "Description", value: vendor.customerDescription || "—" },
              ].map((row, i) => (
                <Box key={i} sx={{ py: 1.25, borderBottom: i < 3 ? "1px solid #f1f5f9" : "none" }}>
                  <Typography fontSize={10} color="#94a3b8" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{row.label}</Typography>
                  <Typography fontSize={13} color="#1e293b" fontWeight={500} mt={0.25}>{row.value}</Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Supplied Products Tab ── */}
      {tab === 1 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography fontSize={14} fontWeight={700} color="#0f172a">{products.length} products linked</Typography>
            <Box sx={{ ml: "auto" }}>
              <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={openAdd}
                sx={{ borderRadius: 2.5, fontWeight: 700, fontSize: 12, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
                Add Product
              </Button>
            </Box>
          </Box>

          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
            {products.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <InventoryIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
                <Typography fontSize={14} color="#94a3b8">No products linked to this supplier yet</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ mt: 2, textTransform: "none", fontWeight: 600, color: "#00838f" }}>Add Product</Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Product</TableCell>
                      <TableCell sx={thSx} align="right">Supplier Price</TableCell>
                      <TableCell sx={thSx} align="center">Unit</TableCell>
                      <TableCell sx={thSx}>Conversion</TableCell>
                      <TableCell sx={thSx} align="right">Min Order</TableCell>
                      <TableCell sx={thSx} align="right">Lead Time</TableCell>
                      <TableCell sx={thSx} align="center">Default</TableCell>
                      <TableCell sx={thSx} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map(sp => (
                      <TableRow key={sp.id} sx={{ "&:hover": { bgcolor: "#fafbfe" } }}>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={13} fontWeight={600} color="#1e293b" sx={{ textTransform: "capitalize" }}>
                            {sp.product?.productName || `Product #${sp.product_id}`}
                          </Typography>
                          {sp.product?.productBarcode && <Typography fontSize={10} color="#94a3b8">{sp.product.productBarcode}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Typography fontSize={14} fontWeight={700} color="#0f172a" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            ${sp.supplier_price.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25 }}>
                          <Chip label={sp.supplier_unit || "—"} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography fontSize={12} color="#64748b">{sp.unit_conversion || "—"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{sp.minimum_order_qty || "—"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Typography fontSize={12} color="#64748b">{sp.lead_time_days ? `${sp.lead_time_days}d` : "—"}</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25 }}>
                          {sp.is_default ? (
                            <Tooltip title="Default supplier"><StarIcon sx={{ fontSize: 18, color: "#f59e0b" }} /></Tooltip>
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25 }}>
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(sp)} sx={{ width: 28, height: 28 }}><EditIcon sx={{ fontSize: 14, color: "#64748b" }} /></IconButton></Tooltip>
                            <Tooltip title="Remove"><IconButton size="small" onClick={() => handleDelete(sp.id)} sx={{ width: 28, height: 28 }}><DeleteIcon sx={{ fontSize: 14, color: "#ef4444" }} /></IconButton></Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* ── Add/Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">{editItem ? "Edit Product Mapping" : "Add Supplied Product"}</Typography>
            <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {!editItem && (
              <Autocomplete
                options={allProducts.filter(p => !products.some(sp => sp.product_id === p.id))}
                getOptionLabel={o => `${o.productName}${o.productBarcode ? ` (${o.productBarcode})` : ""}`}
                value={selectedProduct}
                onChange={(_, v) => { setSelectedProduct(v); if (v) setForm(f => ({ ...f, product_id: v.id })); }}
                renderInput={params => <TextField {...params} size="small" label="Select Product" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />}
              />
            )}
            {editItem && (
              <Typography fontSize={13} color="#64748b" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                {editItem.product?.productName || `Product #${editItem.product_id}`}
              </Typography>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Supplier Price" type="number" value={form.supplier_price}
                  onChange={e => setForm(f => ({ ...f, supplier_price: Number(e.target.value) }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Supplier Unit" value={form.supplier_unit}
                  onChange={e => setForm(f => ({ ...f, supplier_unit: e.target.value }))}
                  placeholder="e.g., box, case, lb"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Unit Conversion" value={form.unit_conversion}
                  onChange={e => setForm(f => ({ ...f, unit_conversion: e.target.value }))}
                  placeholder="e.g., 1 box = 10 lb"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Min Order Qty" type="number" value={form.minimum_order_qty}
                  onChange={e => setForm(f => ({ ...f, minimum_order_qty: Number(e.target.value) }))}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Lead Time (days)" type="number" value={form.lead_time_days}
                  onChange={e => setForm(f => ({ ...f, lead_time_days: Number(e.target.value) }))}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
                  <Switch size="small" checked={form.is_default} onChange={(_, v) => setForm(f => ({ ...f, is_default: v }))} />
                  <Typography fontSize={13} fontWeight={600} color="#64748b">Default Supplier</Typography>
                </Box>
              </Grid>
            </Grid>

            <Button variant="contained" disableElevation fullWidth onClick={handleSave} disabled={saving || (!editItem && !form.product_id)}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2.5, py: 1.1, fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none", mt: 1 }}>
              {editItem ? "Update Mapping" : "Add Product"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default VendorView;
